import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Tags, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { LoadingRows } from "@/components/shared/loading-rows";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { Button } from "@/components/ui/button";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type Category,
  type CategoryMember,
  useAddCompetenciesToCategory,
  useAddSkillsToCategory,
  useCategories,
  useCategoryCompetencies,
  useCategorySkills,
  useCreateCategory,
  useDeleteCategory,
  useRemoveCompetenciesFromCategory,
  useRemoveSkillsFromCategory,
  useReorderCategory,
  useUpdateCategory,
} from "@/hooks/use-categories";
import {
  categoryColorLabel,
  categoryColorVar,
  DEFAULT_CATEGORY_COLOR,
  isHexCategoryColor,
} from "@/lib/category-colors";
import { type CategoryCreateInput, categoryCreateSchema } from "@/schemas/categories";

/**
 * Categories — a group of skills and/or competencies. Replaces what used to
 * be two separate lists here (a single-pick "Skill Category" field, and a
 * many-to-many "Competency Theme" grouping) with one: admins kept reaching
 * for one when they meant the other, and the underlying distinction (a
 * category files one thing, a theme groups several) didn't earn the
 * confusion it caused (Kori Deitz, via Matt, 2026-10-14 — reversing the
 * 2026-10-07 decision to keep them separate).
 *
 * Laid out as a list-and-detail pair, like the Spec board: categories down
 * the left in admin order (same mechanics as every other config list,
 * `named-config-list.ts`), the selected one's skills and competencies side
 * by side on the right, each with its own add-picker that applies on pick
 * (no draft to save) and its own remove per row.
 */
export function SettingsCategoriesPage() {
  const { data: categories = [], isLoading, isError, error, refetch } = useCategories();
  const [dialog, setDialog] = useState<CategoryDialogState>({ type: "none" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const deleteCategory = useDeleteCategory();
  const reorder = useReorderCategory();

  /*
    Land on the first category so the detail side is never pointlessly empty,
    and follow a deletion rather than leaving it pointing at a category
    that's gone.
  */
  const selected = categories.find((c) => c.id === selectedId) ?? categories[0] ?? null;
  useEffect(() => {
    if (selectedId !== null && !categories.some((c) => c.id === selectedId)) setSelectedId(null);
  }, [categories, selectedId]);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load categories"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr] lg:items-start">
      <DetailSection
        title="Categories"
        headerActions={
          <Button size="sm" onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            Add category
          </Button>
        }
      >
        {isLoading ? (
          <LoadingRows />
        ) : categories.length === 0 ? (
          <EmptyState
            icon={Tags}
            title="No categories"
            description="Create the first category to group skills and competencies."
          />
        ) : (
          <ul className="-mx-6 divide-y border-t">
            {categories.map((category, index) => (
              <li key={category.id}>
                <div
                  className={`flex items-center gap-2 px-4 py-1.5 transition-colors ${
                    selected?.id === category.id ? "bg-muted" : "hover:bg-muted/30"
                  }`}
                >
                  <ReorderButtons
                    label={category.name}
                    disableUp={index === 0 || reorder.isPending}
                    disableDown={index === categories.length - 1 || reorder.isPending}
                    onMove={(direction) => reorder.mutate({ id: category.id, direction })}
                  />
                  <button
                    type="button"
                    onClick={() => setSelectedId(category.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
                  >
                    <CategorySwatch color={category.color} dotOnly />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {category.name}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {category.skillCount +
                        category.competencyCount +
                        category.reviewCriterionCount}
                    </span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DetailSection>

      {selected && (
        <CategoryDetail
          key={`detail-${selected.id}`}
          category={selected}
          onEdit={() => setDialog({ type: "edit", category: selected })}
          onDelete={() => setDialog({ type: "delete", category: selected })}
        />
      )}

      {dialog.type === "create" && (
        <CategoryDialog open onOpenChange={() => setDialog({ type: "none" })} />
      )}
      {dialog.type === "edit" && (
        <CategoryDialog
          key={`edit-${dialog.category.id}`}
          open
          onOpenChange={() => setDialog({ type: "none" })}
          category={dialog.category}
        />
      )}
      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title="Delete category?"
          description={
            <>
              Permanently deletes {dialog.category.name}.{" "}
              {categoryUsageParts(dialog.category).length > 0
                ? `${categoryUsageParts(dialog.category).join(" and ")} still use it, so the delete will be refused — move them first.`
                : "Nothing references this category."}
            </>
          }
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteCategory.mutate(dialog.category.id, {
              onSuccess: () => setDialog({ type: "none" }),
            });
          }}
        />
      )}
    </div>
  );
}

type CategoryDialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; category: Category }
  | { type: "delete"; category: Category };

/** What's standing in the way of a delete, phrased per kind — skills, competencies, review questions. */
function categoryUsageParts(category: Category): string[] {
  return [
    category.skillCount > 0
      ? `${category.skillCount} skill${category.skillCount === 1 ? "" : "s"}`
      : null,
    category.competencyCount > 0
      ? `${category.competencyCount} ${category.competencyCount === 1 ? "competency" : "competencies"}`
      : null,
    category.reviewCriterionCount > 0
      ? `${category.reviewCriterionCount} review ${category.reviewCriterionCount === 1 ? "question" : "questions"}`
      : null,
  ].filter((part): part is string => part !== null);
}

function CategoryDialog({
  open,
  onOpenChange,
  category,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: Category;
}) {
  const isEdit = category != null;
  const create = useCreateCategory();
  const update = useUpdateCategory(category?.id ?? "");
  const form = useForm<CategoryCreateInput>({
    resolver: zodResolver(categoryCreateSchema),
    defaultValues: {
      name: category?.name ?? "",
      description: category?.description ?? "",
      color: category?.color ?? null,
    },
  });

  async function onSubmit(values: CategoryCreateInput) {
    const payload = {
      ...values,
      description: values.description?.trim() || null,
    };
    if (isEdit) await update.mutateAsync(payload);
    else await create.mutateAsync(payload);
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit category" : "Add category"}
      description={
        isEdit
          ? "Update this category."
          : "Add a new category to group skills and competencies under."
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={isEdit ? "Save" : "Create"}
      pending={pending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Programming languages" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                value={field.value ?? ""}
                rows={3}
                placeholder="Optional short note"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="color"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Colour (optional)</FormLabel>
            {/*
              A native colour input, not a fixed palette — the swatch an
              admin picks per category to tell groups apart at a glance, not
              a semantic status colour, so there's no reason to restrict it
              to the brand's theme tokens (see design-system.md). Skips
              FormControl: it clones onto a single child, and this is three
              (the picker, the hex field, and a clear button).
            */}
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Pick a colour"
                aria-invalid={Boolean(fieldState.error)}
                // A <input type="color"> only accepts a 6-digit hex value —
                // a legacy token from before the palette widened would
                // otherwise silently reset the picker to black.
                value={
                  field.value && isHexCategoryColor(field.value)
                    ? field.value
                    : DEFAULT_CATEGORY_COLOR
                }
                onChange={(e) => field.onChange(e.target.value)}
                className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-0.5"
              />
              <Input
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value.trim() || null)}
                placeholder={DEFAULT_CATEGORY_COLOR}
                aria-invalid={Boolean(fieldState.error)}
                className="font-mono"
              />
              {field.value && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => field.onChange(null)}
                >
                  Clear
                </Button>
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}

/**
 * The category's colour. Shows a dash rather than an empty cell when unset,
 * so it reads as "no colour" instead of "not loaded". `dotOnly` drops the
 * text label for the narrow category-list row, where the name itself is the
 * identifying text and a label per swatch would just repeat it down the list.
 */
function CategorySwatch({ color, dotOnly }: { color: string | null; dotOnly?: boolean }) {
  const value = categoryColorVar(color);
  if (!value) return dotOnly ? null : <span className="text-muted-foreground">—</span>;
  const dot = dotOnly ? (
    <span
      role="img"
      aria-label={categoryColorLabel(color) ?? undefined}
      className="inline-block size-3 shrink-0 rounded-full border border-border"
      style={{ backgroundColor: value }}
    />
  ) : (
    <span
      className="inline-block size-3 shrink-0 rounded-full border border-border"
      style={{ backgroundColor: value }}
      aria-hidden
    />
  );
  if (dotOnly) return dot;
  // The swatch alone is indistinguishable to anyone who cannot separate the
  // hues, so the name rides alongside it wherever there's room to spell it out.
  return (
    <span className="flex items-center gap-2">
      {dot}
      <span className="text-muted-foreground text-sm">{categoryColorLabel(color)}</span>
    </span>
  );
}

/**
 * The selected category's detail — name, colour and description, with
 * edit/delete in the header — plus its skills and competencies side by side
 * below. Each picker adds on select and clears itself, so filing a run of
 * items is pick, pick, pick with nothing to confirm — same pattern the old
 * Themes screen used for competencies, now doubled for skills too.
 *
 * No `code` here: it's a stable internal key for one backend lookup
 * (`scripts/seed-review-criteria.ts`'s "Uncategorized" fallback), not
 * something an admin ever needs to read or set.
 */
function CategoryDetail({
  category,
  onEdit,
  onDelete,
}: {
  category: Category;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="space-y-4">
      <DetailSection
        title={category.name}
        headerActions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={onDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          {category.color && <CategorySwatch color={category.color} />}
          {category.description && (
            <p className="text-sm text-muted-foreground">{category.description}</p>
          )}
        </div>
      </DetailSection>

      <div className="grid gap-4 sm:grid-cols-2">
        <CategoryMemberList
          categoryId={category.id}
          kind="skills"
          entity="skills"
          label="Skills"
          addPlaceholder="Add a skill…"
          searchPlaceholder="Search skills…"
          emptyLabel="No skills in this category yet."
        />
        <CategoryMemberList
          categoryId={category.id}
          kind="competencies"
          entity="competencies"
          label="Competencies"
          addPlaceholder="Add a competency…"
          searchPlaceholder="Search competencies…"
          emptyLabel="No competencies in this category yet."
        />
      </div>
    </div>
  );
}

function useMembersForKind(categoryId: string, kind: "skills" | "competencies") {
  const skills = useCategorySkills(kind === "skills" ? categoryId : null);
  const competencies = useCategoryCompetencies(kind === "competencies" ? categoryId : null);
  return kind === "skills" ? skills : competencies;
}

function useAddForKind(categoryId: string, kind: "skills" | "competencies") {
  const addSkills = useAddSkillsToCategory(categoryId);
  const addCompetencies = useAddCompetenciesToCategory(categoryId);
  return kind === "skills" ? addSkills : addCompetencies;
}

function useRemoveForKind(categoryId: string, kind: "skills" | "competencies") {
  const removeSkills = useRemoveSkillsFromCategory(categoryId);
  const removeCompetencies = useRemoveCompetenciesFromCategory(categoryId);
  return kind === "skills" ? removeSkills : removeCompetencies;
}

function CategoryMemberList({
  categoryId,
  kind,
  entity,
  label,
  addPlaceholder,
  searchPlaceholder,
  emptyLabel,
}: {
  categoryId: string;
  kind: "skills" | "competencies";
  entity: "skills" | "competencies";
  label: string;
  addPlaceholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
}) {
  const {
    data: members = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useMembersForKind(categoryId, kind);
  const add = useAddForKind(categoryId, kind);
  const remove = useRemoveForKind(categoryId, kind);

  return (
    <DetailSection title={label} className="gap-3">
      <div className="space-y-2">
        <EntityCombobox
          entity={entity}
          value={null}
          /* Applies on pick — there is no draft to save, which is the point. */
          onChange={(id) => {
            if (id) add.mutate([id]);
          }}
          /* Already in this category, so offering it again would be a no-op row. */
          excludeIds={members.map((m: CategoryMember) => m.id)}
          disabled={add.isPending}
          placeholder={addPlaceholder}
          searchPlaceholder={searchPlaceholder}
        />

        {isError ? (
          <ErrorState
            className="py-4"
            message={error?.message ?? `Couldn't load this category's ${label.toLowerCase()}.`}
            onRetry={refetch}
          />
        ) : isLoading ? (
          <LoadingRows />
        ) : members.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {members.map((member: CategoryMember) => (
              <li key={member.id} className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-sm">{member.name}</span>
                  {member.description && (
                    <span className="block truncate text-2xs text-muted-foreground">
                      {member.description}
                    </span>
                  )}
                </span>
                <LifecycleBadge status={member.status} />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${member.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate([member.id])}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DetailSection>
  );
}
